import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Building2, UserCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, roleHome } from "@/lib/api";
import { cn } from "@/lib/utils";
import AuthShell from "@/pages/auth/AuthShell";

const TABS = [
  { key: "employer", icon: Building2 },
  { key: "worker", icon: UserCircle },
  { key: "admin", icon: ShieldCheck },
];

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const [type, setType] = useState("employer");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email }); toast.success(t("vrf.sentSub", { email })); }
    catch { toast.error("Error"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNeedsVerify(false);
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t("login.welcome"));
      const ret = localStorage.getItem("eg_return");
      if (ret) { localStorage.removeItem("eg_return"); nav(ret, { replace: true }); }
      else nav(roleHome(user.role), { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "EMAIL_NOT_VERIFIED") { setNeedsVerify(true); setError(t("vrf.notVerified")); }
      else { setError(formatApiErrorDetail(detail) || err.message); }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("login.title")} subtitle={t("login.subtitle")} testid="login-page"
      footer={<>{t("login.newEmployer")} <Link to="/register" className="font-semibold text-cyan-dark hover:underline">{t("login.create")}</Link></>}>
      <div className="mb-5">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{t("ltabs.choose")}</div>
        <div className="grid grid-cols-3 gap-2">
          {TABS.map((tb) => (
            <button key={tb.key} type="button" onClick={() => setType(tb.key)} data-testid={`login-type-${tb.key}`}
              className={cn("flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-xs font-medium transition-colors",
                type === tb.key ? "border-cyan bg-cyan/10 text-cyan-dark" : "border-slate-200 text-slate-500 hover:bg-slate-50")}>
              <tb.icon className="h-5 w-5" /> {t(`ltabs.${tb.key}`)}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400" data-testid="login-type-hint">{t(`ltabs.hint${type.charAt(0).toUpperCase() + type.slice(1)}`)}</p>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">{t("login.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" placeholder="you@company.com" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t("login.password")}</Label>
            <Link to="/forgot-password" className="text-xs text-cyan-dark hover:underline" data-testid="login-forgot-link">{t("login.forgot")}</Link>
          </div>
          <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" placeholder="••••••••" />
        </div>
        {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="login-error">{error}{needsVerify && <> · <button type="button" onClick={resend} className="font-semibold underline" data-testid="login-resend-verify">{t("register.resend") || "Resend"}</button></>}</div>}
        <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full rounded-full bg-navy text-white hover:bg-navy-muted">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("login.signingIn")}</> : t("login.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
